import { showError } from "toast/toast";
import { clearAuthCookies,getAuthCookie } from "./authCookies";

/**
 * Token Expiration Utility
 * Decodes JWT token and checks if it's expired
 */

interface DecodedToken {
  exp?: number; // Expiration timestamp (Unix time)
  iat?: number; // Issued at timestamp
  [key: string]: any; // Other token claims
}

/**
 * Decodes a JWT token without verification
 * @param token - JWT token string
 * @returns Decoded token payload or null if invalid
 */
export function decodeToken(token: string): DecodedToken | null {
  try {
    // JWT has 3 parts: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    
    // Add padding if needed (base64url decoding)
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    
    // Decode from base64
    const decoded = atob(padded);

    // console.log('Decoded token payload:', decoded);
    
    // Parse JSON
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

/**
 * Checks if a token is expired
 * @param token - JWT token string
 * @returns true if token is expired or invalid, false if valid
 */
export function isTokenExpired(token: string | null): boolean {
  if (!token) {
    return true; // No token means expired
  }

  const decoded = decodeToken(token);
  if (!decoded) {
    return true; // Invalid token means expired
  }

  // Check if token has expiration claim
  if (!decoded.exp) {
    // If no expiration claim, assume it's valid (some tokens don't expire)
    return false;
  }

  // Compare expiration time with current time
  // exp is in seconds, Date.now() is in milliseconds
  const currentTime = Math.floor(Date.now() / 1000);
  const isExpired = decoded.exp < currentTime;
  
  return isExpired;
}

/**
 * Gets the expiration time of a token
 * @param token - JWT token string
 * @returns Expiration date or null if invalid/not found
 */
export function getTokenExpiration(token: string | null): Date | null {
  if (!token) {
    return null;
  }

  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return null;
  }

  // Convert Unix timestamp to Date (exp is in seconds)
  return new Date(decoded.exp * 1000);
}

/**
 * Clears all authentication data from localStorage
 */
export function clearAuthData(): void {
  clearAuthCookies();
  localStorage.removeItem("re_signage_unique_id");
  localStorage.removeItem("re_installation_unique_id");
}

// Flag to prevent multiple redirects
let isRedirecting = false;

/**
 * Reset the redirect flag (call this after successful login)
 */
export function resetRedirectFlag(): void {
  isRedirecting = false;
}

/**
 * Checks token and redirects if expired
 * @returns true if token is valid, false if expired (and redirected)
 */
export function checkTokenAndRedirect(): boolean {
  // Prevent multiple redirects
  if (isRedirecting) {
    return false;
  }

  const token = getAuthCookie("sessionToken");
  
  if (isTokenExpired(token)) {
    console.log('⚠️ Token expired! Clearing auth data and redirecting to home page...');
    
    // Set flag to prevent multiple redirects
    isRedirecting = true;
    
    // Show error message to user
    showError("Your session has expired. Please login again.");
    
    // Clear all auth data
    clearAuthData();
    
    // Use replace instead of href to prevent back button issues
    // Add delay to show error message before redirecting (2 seconds)
    setTimeout(() => {
      window.location.replace("/login");
    }, 2000);
    
    return false;
  }
  
  return true;
}

